// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------
import React from "react";

// -----------------------------------------------------------------------------
// Components
// -----------------------------------------------------------------------------
import { Button } from "components/Common/Button";

// -----------------------------------------------------------------------------
// Utils and Styles
// -----------------------------------------------------------------------------
import StyledCreateWorkoutForm from "./styles";
import { useGetDomainGymQuery, useGetCoachesQuery, useCreateCoachMutation } from "store/api/api";
import { useRecoilValue } from "recoil";
import { userAtom } from "globalAtoms";
import { useParams } from "react-router";
import { validateRequiredEncodedLength, validateEncodedLength } from "helpers/Form/commonFormValidations";
import { decodeString, encodeString, sortByProperty, validTrainers } from "utils";
import { workoutTypes } from "helpers/constants";
import { Form } from "antd";
import { FormInput } from "components/Common/Form/FormInput";
import { FormTextArea } from "components/Common/Form/FormTextArea";
import { FormTagSelect } from "components/Common/Form/FormTagSelect";

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
const CreateWorkoutForm = ({ nextStep, values, loading }) => {
	const user = useRecoilValue(userAtom);
	const [form] = Form.useForm();
	const { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const [createCoachTrigger, createCoach] = useCreateCoachMutation();
	const domainGymId = domainGymResponse.data?.id;
	const {
		data: coachesData,
		isLoading: isCoachesLoading,
		refetch,
	} = useGetCoachesQuery(domainGymId, {
		skip: !domainGymId,
	});

	const handleFormSubmit = async (formObj) => {
		const { workoutTitle, workoutShortDescription, workoutDescription, workoutCoach } = formObj;
		const coaches = await validTrainers({
			domainGymId,
			coachesData,
			workoutCoach,
			createCoachTrigger,
		});
		await nextStep({
			gym_id: domainGymId,
			user_id: user?.id,
			name: encodeString(workoutTitle),
			coaches,
			description: encodeString(workoutDescription),
			short_description: encodeString(workoutShortDescription),
			is_free: 0,
			type: workoutTypes.ON_DEMAND,
		});
		await refetch();
	};

	const coachOptions = React.useMemo(() => {
		if (coachesData?.coaches) {
			return sortByProperty(coachesData?.coaches, "name")
				?.filter(({ status }) => status === "active")
				?.map((coach) => ({
					label: `${decodeString(coach?.name)}`,
					value: coach?.id,
				}));
		}
		return [];
	}, [coachesData?.coaches]);
	return (
		<StyledCreateWorkoutForm>
			<div className="sb-create-workout-form">
				<Form
					onFinish={handleFormSubmit}
					layout="vertical"
					className="sb-create-workout-form__form"
					form={form}
					initialValues={{
						workoutTitle: decodeString(values.name),
						workoutDescription: decodeString(values.description),
						workoutCoach: values.coaches,
						workoutShortDescription: values.short_description,
					}}
					requiredMark
				>
					<FormInput
						name="workoutTitle"
						rules={[
							{ required: true, message: "This field is required" },
							() => ({
								validator(_, value) {
									const validationStatus = validateRequiredEncodedLength(value, 255);
									if (value && validationStatus) {
										return Promise.reject(new Error(validationStatus));
									}
									return Promise.resolve();
								},
							}),
						]}
						label="Workout Title"
						size="small"
						autoFocus
					/>
					<FormTextArea
						name="workoutShortDescription"
						label="Workout Short Description"
						placeholder="Workout Short Description"
						rules={[
							() => ({
								validator(_, value) {
									const validationStatus = validateEncodedLength(value, 100);
									if (validationStatus) {
										return Promise.reject(new Error(validationStatus));
									}
									return Promise.resolve();
								},
							}),
						]}
						rows={2}
					/>
					<FormTextArea
						name="workoutDescription"
						label="Workout Description"
						placeholder="Workout Description"
						rules={[
							() => ({
								required: true,
								validator(_, value) {
									const validationStatus = validateRequiredEncodedLength(value, 2048);
									if (validationStatus) {
										return Promise.reject(new Error(validationStatus));
									}
									return Promise.resolve();
								},
							}),
						]}
						rows={4}
					/>
					{coachesData?.coaches && (
						<FormTagSelect
							name="workoutCoach"
							options={coachOptions}
							label="Workout Trainer"
							placeholder="Workout Trainer"
							rules={[
								{ required: true, message: "This field is required" },
								() => ({
									required: true,
									validator(_, value) {
										if (value && Array.isArray(value)) {
											const longTags = value.filter((tag) => tag.length >= 256);
											if (longTags.length > 0) {
												return Promise.reject(new Error(`Maximum allowed characters is 255 per trainer.`));
											}
										}
										return Promise.resolve();
									},
								}),
								() => ({
									required: true,
									validator(_, value) {
										if (value && Array.isArray(value)) {
											const inactiveCoaches = coachesData?.coaches
												.filter((coach) => coach.status !== "active")
												.map((coach) => coach.name.toLowerCase());
											const invalidTags = value.filter((tag) => inactiveCoaches.includes(tag.toLowerCase()));
											if (invalidTags.length > 0) {
												return Promise.reject(
													new Error(`Select inactive trainers is not allowed. (${invalidTags.join(", ")})`)
												);
											}
										}
										return Promise.resolve();
									},
								}),
							]}
							showSearch
							loading={isCoachesLoading}
						/>
					)}
					<Button
						type="submit"
						className="sb-create-workout-form__form__button primary-filled"
						disabled={loading || createCoach.isLoading}
						loading={loading || createCoach.isLoading}
						data-id="btnSubmit"
						style={{ maxWidth: "unset" }}
					>
						Next
					</Button>
				</Form>
			</div>
		</StyledCreateWorkoutForm>
	);
};

export default CreateWorkoutForm;

